import React, { useState, useEffect } from 'react';
import {
  ChatBubbleLeftRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  PaperAirplaneIcon
} from '@heroicons/react/24/outline';
import { adminAPI } from '../../services/api';

interface SupportTicket {
  id: number;
  ticket_number: string;
  user_id: number;
  user_name: string;
  user_email: string;
  subject: string;
  message: string;
  status: string;
  priority: string;
  category?: string;
  admin_id?: number;
  admin_name?: string;
  admin_response?: string;
  responded_at?: string;
  resolved_at?: string;
  created_at: string;
  updated_at: string;
}

const AdminSupport: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [adminResponse, setAdminResponse] = useState('');
  const [responding, setResponding] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchTickets();
  }, [filterStatus]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getSupportTickets(filterStatus);
      setTickets(response.tickets);
    } catch (err: any) {
      setError('Failed to load support tickets');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRespondToTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedTicket || !adminResponse.trim()) {
      setError('Please enter a response');
      return;
    }

    setResponding(true);
    setError('');

    try {
      await adminAPI.respondToSupportTicket(selectedTicket.id, {
        admin_response: adminResponse,
        status: 'resolved'
      });
      
      setSuccess('Response sent successfully!');
      setShowResponseModal(false);
      setAdminResponse('');
      setSelectedTicket(null);
      fetchTickets();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send response');
    } finally {
      setResponding(false);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'bg-yellow-100 text-yellow-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityBadgeColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />;
      case 'in_progress':
        return <ClockIcon className="h-5 w-5 text-blue-600" />;
      case 'resolved':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'closed':
        return <XMarkIcon className="h-5 w-5 text-gray-600" />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center mb-2">
            <ChatBubbleLeftRightIcon className="h-8 w-8 text-indigo-500 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900 font-poppins">Support Center</h1>
          </div>
          <p className="text-gray-600 font-poppins">
            Manage and respond to user support tickets
          </p>
        </div>

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-volexx-lg p-4 mb-6">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
              <p className="text-sm font-medium text-green-800 font-poppins">{success}</p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-volexx-lg p-4 mb-6">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-sm font-medium text-red-800 font-poppins">{error}</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-volexx-lg shadow-volexx-md mb-6 p-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                Filter by Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-indigo-500 font-poppins"
              >
                <option value="">All Tickets</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={fetchTickets}
                className="px-6 py-2 bg-indigo-600 text-white rounded-volexx hover:bg-indigo-700 transition-colors font-poppins font-semibold"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : tickets.length === 0 ? (
          <div className="bg-white rounded-volexx-lg shadow-volexx-md p-12 text-center">
            <ChatBubbleLeftRightIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-900 mb-2 font-poppins">No Support Tickets</h3>
            <p className="text-gray-600 font-poppins">
              {filterStatus ? `No ${filterStatus} tickets found` : 'No support tickets available'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-volexx-lg shadow-volexx-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                      Ticket
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                      Subject
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider font-poppins">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tickets.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-gray-900 font-poppins">
                          {ticket.ticket_number}
                        </div>
                        {ticket.category && (
                          <div className="text-xs text-gray-500 font-poppins capitalize">
                            {ticket.category}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-gray-900 font-poppins">
                          {ticket.user_name}
                        </div>
                        <div className="text-xs text-gray-500 font-poppins">
                          {ticket.user_email}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 font-poppins max-w-xs truncate">
                          {ticket.subject}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-volexx font-poppins capitalize ${getPriorityBadgeColor(ticket.priority)}`}>
                          {ticket.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(ticket.status)}
                          <span className={`ml-2 px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-volexx font-poppins capitalize ${getStatusBadgeColor(ticket.status)}`}>
                            {ticket.status.replace('_', ' ')}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-poppins">
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setShowResponseModal(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 font-poppins font-semibold"
                        >
                          View & Respond
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showResponseModal && selectedTicket && (
          <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
                setShowResponseModal(false);
                setAdminResponse('');
                setError('');
              }}></div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-volexx-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-2xl font-bold text-gray-900 font-poppins">
                      Support Ticket: {selectedTicket.ticket_number}
                    </h3>
                    <button
                      onClick={() => {
                        setShowResponseModal(false);
                        setAdminResponse('');
                        setError('');
                      }}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div>
                      <p className="text-sm text-gray-500 font-poppins">User</p>
                      <p className="text-sm font-semibold text-gray-900 font-poppins">{selectedTicket.user_name}</p>
                      <p className="text-xs text-gray-500 font-poppins">{selectedTicket.user_email}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-poppins">Created</p>
                      <p className="text-sm font-semibold text-gray-900 font-poppins">
                        {new Date(selectedTicket.created_at).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-poppins">Priority</p>
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-volexx font-poppins capitalize ${getPriorityBadgeColor(selectedTicket.priority)}`}>
                        {selectedTicket.priority}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 font-poppins">Status</p>
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-volexx font-poppins capitalize ${getStatusBadgeColor(selectedTicket.status)}`}>
                        {selectedTicket.status.replace('_', ' ')}
                      </span>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                      Subject
                    </label>
                    <p className="text-sm text-gray-900 font-poppins">{selectedTicket.subject}</p>
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                      User Message
                    </label>
                    <div className="bg-gray-50 rounded-volexx p-4">
                      <p className="text-sm text-gray-900 whitespace-pre-wrap font-poppins">{selectedTicket.message}</p>
                    </div>
                  </div>

                  {selectedTicket.admin_response && (
                    <div className="mb-6">
                      <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                        Admin Response
                      </label>
                      <div className="bg-green-50 rounded-volexx p-4">
                        <p className="text-sm text-gray-900 whitespace-pre-wrap font-poppins">{selectedTicket.admin_response}</p>
                        {selectedTicket.admin_name && (
                          <p className="text-xs text-gray-500 mt-2 font-poppins">
                            Response by {selectedTicket.admin_name} on {new Date(selectedTicket.responded_at!).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {selectedTicket.status !== 'resolved' && selectedTicket.status !== 'closed' && (
                    <form onSubmit={handleRespondToTicket}>
                      <div className="mb-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-2 font-poppins">
                          Your Response
                        </label>
                        <textarea
                          value={adminResponse}
                          onChange={(e) => setAdminResponse(e.target.value)}
                          placeholder="Enter your response to the user..."
                          rows={4}
                          className="w-full px-4 py-3 border border-gray-300 rounded-volexx focus:outline-none focus:ring-2 focus:ring-indigo-500 font-poppins"
                          disabled={responding}
                          required
                        />
                      </div>

                      <div className="flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={() => {
                            setShowResponseModal(false);
                            setAdminResponse('');
                            setError('');
                          }}
                          className="px-6 py-2 bg-gray-200 text-gray-700 rounded-volexx hover:bg-gray-300 transition-colors font-poppins font-semibold"
                          disabled={responding}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="px-6 py-2 bg-indigo-600 text-white rounded-volexx hover:bg-indigo-700 transition-colors font-poppins font-semibold disabled:opacity-50 flex items-center"
                          disabled={responding}
                        >
                          {responding ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Sending...
                            </>
                          ) : (
                            <>
                              <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                              Send Response
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSupport;
